import { useState } from "react";
import { useAdmin } from "../../../contexts/AdminContext";

export default function ContactForm() {
  const { settings } = useAdmin();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const channels = [
    {
      icon: "ri-mail-send-line",
      title: "E-Mail",
      value: settings.contactEmail || "info@germantraumhauslotterie.de",
      sub: "Antwort innerhalb von 2 Std. an Werktagen",
      color: "bg-amber-50 text-amber-600",
    },
    {
      icon: "ri-phone-line",
      title: "Telefon",
      value: settings.contactPhone || "+49 (0) 40 123 456 78",
      sub: "Mo–Fr · 9:00–18:00 Uhr",
      color: "bg-emerald-50 text-secondary-dark",
    },
    {
      icon: "ri-map-pin-line",
      title: "Adresse",
      value: settings.contactAddress || "Dreamify gGmbH, Hamburg, Deutschland",
      sub: "GGL-zugelassen · Lizenz 2024",
      color: "bg-slate-100 text-slate-600",
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d6upu5dt4s7j0tv5i83g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        form.reset();
        setCharCount(0);
        setSubmitted(true);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

          {/* Left: info + channels */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <span className="text-yellow-500 text-xs font-bold tracking-[0.25em] uppercase">Kontaktieren Sie uns</span>
              <h2 className="font-playfair text-4xl font-bold text-slate-900 mt-3 mb-4">
                Keine passende<br />Antwort gefunden?
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Unser Support-Team steht Ihnen 7 Tage die Woche zur Verfügung.
                Füllen Sie das Formular aus oder kontaktieren Sie uns direkt.
              </p>
            </div>

            <div className="space-y-4">
              {channels.map((c) => (
                <div key={c.title} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${c.color}`}>
                    <i className={`${c.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{c.title}</p>
                    <p className="text-slate-800 font-semibold text-sm mt-0.5">{c.value}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{c.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Response time badge */}
            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100">
              <div className="relative flex-shrink-0">
                <div className="w-3 h-3 bg-secondary-light rounded-full"></div>
                <div className="absolute inset-0 w-3 h-3 bg-secondary-light rounded-full animate-ping opacity-60"></div>
              </div>
              <p className="text-sm text-slate-600">
                <strong className="text-slate-800">Online-Support</strong> — aktuelle Antwortzeit&nbsp;: <strong className="text-secondary-dark">~45 min</strong>
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-100 p-8">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50 mb-5">
                    <i className="ri-check-line text-3xl text-secondary"></i>
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-2">Nachricht gesendet!</h3>
                  <p className="text-slate-500 text-sm max-w-xs leading-relaxed">
                    Wir werden Ihnen innerhalb von 2 Stunden antworten. Bitte prüfen Sie auch Ihren Spam-Ordner.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Eine weitere Nachricht senden
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-bold text-slate-900 text-xl mb-1">Kontaktformular</h3>
                  <p className="text-slate-500 text-sm mb-6">Alle Felder sind Pflichtfelder.</p>

                  <form
                    data-readdy-form
                    id="contact_main_form"
                    action="https://readdy.ai/api/form/d6upu5dt4s7j0tv5i83g"
                    method="POST"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1.5">Vorname</label>
                        <input
                          type="text"
                          name="firstname"
                          required
                          placeholder="Marie"
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:bg-white transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1.5">Nachname</label>
                        <input
                          type="text"
                          name="lastname"
                          required
                          placeholder="Mustermann"
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:bg-white transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1.5">E-Mail-Adresse</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="marie@beispiel.de"
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-slate-600 block mb-1.5">Betreff</label>
                      <select
                        name="subject"
                        required
                        defaultValue=""
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 focus:bg-white transition-all cursor-pointer"
                      >
                        <option value="" disabled>Wählen Sie einen Betreff…</option>
                        <option value="Participation & tickets">Teilnahme &amp; Lose</option>
                        <option value="Paiement & commande">Zahlung &amp; Bestellung</option>
                        <option value="Mon compte">Mein Konto</option>
                        <option value="Résultats & gains">Ergebnisse &amp; Gewinne</option>
                        <option value="Problème technique">Technisches Problem</option>
                        <option value="Autre">Sonstiges</option>
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-600">Ihre Nachricht</label>
                        <span className={`text-xs ${charCount > 450 ? "text-red-400" : "text-slate-400"}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Beschreiben Sie Ihre Frage im Detail…"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-yellow-400 focus:bg-white transition-all resize-none"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-slate-900 font-bold py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          Wird gesendet…
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-base"></i>
                          Nachricht senden
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-400 text-center leading-relaxed">
                      Durch das Absenden dieses Formulars akzeptieren Sie unsere{" "}
                      <a href="#" className="underline hover:text-slate-600 cursor-pointer">Datenschutzerklärung</a>.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
